<section class="products-dashboard">
  <header class="admin-header">
    <div>
      <h1>Products</h1>
      <p>Manage your products and by-products</p>
    </div>

    <div class="header-actions">
      <button type="button" class="icon-button" aria-label="Notifications">🔔</button>
      <button type="button" class="primary-button" (click)="openAddProduct()" [disabled]="isLoading || isSaving">
        Add Product
      </button>
    </div>
  </header>

    <p *ngIf="errorMessage" class="empty-state">{{ errorMessage }}</p>

    <section class="stats-grid" aria-label="Product stats">
      <article>
        <span>Total Products</span>
        <strong>{{ stats.totalProducts }}</strong>
      </article>
      <article>
        <span>Parent Products</span>
        <strong>{{ stats.parentProducts }}</strong>
      </article>
      <article>
        <span>Child Products</span>
        <strong>{{ stats.childProducts }}</strong>
      </article>
      <article>
        <span>Categories</span>
        <strong>{{ stats.categories }}</strong>
      </article>
    </section>

    <section class="products-card">
      <header class="products-card__header">
        <div>
          <h2>All Products</h2>
          <p>Parent rows include their child product hierarchy.</p>
        </div>
        <button type="button" class="filter-button">
          <span aria-hidden="true">☰</span>
          Filter
        </button>
      </header>

      <div class="table-wrap products-table-wrapper">
        <div class="empty-state" *ngIf="isLoading && !products.length">
          <h3>Loading products...</h3>
          <p>Fetching the latest product catalog.</p>
        </div>

        <table class="products-table" *ngIf="products.length; else emptyProducts">
          <thead>
            <tr>
              <th>Product</th>
              <th>Type</th>
              <th>Category</th>
              <th>Status</th>
              <th>Child Products</th>
              <th>Created On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let parent of paginatedParents; trackBy: trackByProduct">
              <tr>
                <td>
                  <div class="product-cell">
                    <img class="product-thumbnail" [src]="parent.imageUrl" [alt]="parent.name" />
                    <div class="product-info">
                      <strong class="product-name">{{ parent.name }}</strong>
                      <small class="product-description">{{ parent.shortDescription }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="type-badge">Parent</span>
                </td>
                <td>{{ parent.category }}</td>
                <td>
                  <span class="status-badge" [class.status-badge--inactive]="parent.status === 'inactive'">
                    {{ parent.status }}
                  </span>
                </td>
                <td>{{ getChildProducts(parent.id).length }}</td>
                <td>{{ formatDate(parent.createdAt) }}</td>
                <td>
                  <div class="row-actions action-buttons">
                    <button
                      type="button"
                      class="action-btn edit edit-button"
                      (click)="openEditProduct(parent)"
                      [disabled]="isSaving || isDeleting"
                    >
                      <span aria-hidden="true">✎</span>
                      Edit
                    </button>
                    <button
                      type="button"
                      class="action-btn delete delete-button"
                      (click)="confirmDelete(parent)"
                      [disabled]="isSaving || isDeleting"
                    >
                      <span aria-hidden="true">×</span>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
              <tr class="child-row" *ngFor="let child of getChildProducts(parent.id); trackBy: trackByProduct">
                <td>
                  <div class="product-cell product-cell--child">
                    <img class="product-thumbnail" [src]="child.imageUrl" [alt]="child.name" />
                    <div class="product-info">
                      <strong class="product-name">{{ child.name }}</strong>
                      <small class="product-description">{{ child.shortDescription }}</small>
                    </div>
                  </div>
                </td>
                <td>
                  <span class="type-badge type-badge--child">Child</span>
                </td>
                <td>{{ child.category }}</td>
                <td>
                  <span class="status-badge" [class.status-badge--inactive]="child.status === 'inactive'">
                    {{ child.status }}
                  </span>
                </td>
                <td>—</td>
                <td>{{ formatDate(child.createdAt) }}</td>
                <td>
                  <div class="row-actions action-buttons">
                    <button
                      type="button"
                      class="action-btn edit edit-button"
                      (click)="openEditProduct(child)"
                      [disabled]="isSaving || isDeleting"
                    >
                      <span aria-hidden="true">✎</span>
                      Edit
                    </button>
                    <button
                      type="button"
                      class="action-btn delete delete-button"
                      (click)="confirmDelete(child)"
                      [disabled]="isSaving || isDeleting"
                    >
                      <span aria-hidden="true">×</span>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>

        <ng-template #emptyProducts>
          <div class="empty-state" *ngIf="!isLoading && !products.length">
            <h3>No products found.</h3>
            <p>Click Add Product to create your first product.</p>
          </div>
        </ng-template>
      </div>

      <footer class="products-footer" *ngIf="products.length">
        <span>Showing {{ showingStart }}-{{ showingEnd }} of {{ parentProducts.length }} parent products</span>
        <div class="pagination-controls">
          <button type="button" (click)="goToPreviousPage()" [disabled]="currentPage === 1">Previous</button>
          <span>Page {{ currentPage }} of {{ totalPages }}</span>
          <button type="button" (click)="goToNextPage()" [disabled]="currentPage === totalPages">Next</button>
        </div>
      </footer>
    </section>
  <app-product-form-modal
    *ngIf="isFormOpen"
    [product]="editingProduct"
    [parentProducts]="activeParentProducts"
    [nextDisplayOrder]="nextDisplayOrder"
    (saveProduct)="saveProduct($event)"
    (cancel)="closeForm()"
  />

  <div class="confirm-backdrop" *ngIf="deletingProduct" role="presentation">
    <section class="confirm-modal" role="dialog" aria-modal="true" aria-label="Delete product">
      <h2>Delete {{ deletingProduct.name }}?</h2>
      <p *ngIf="deletingProduct.type === 'parent'">
        This parent product and its child products will be removed from the dashboard and public carousel.
      </p>
      <p *ngIf="deletingProduct.type === 'child'">
        This child product will be removed from its parent hierarchy.
      </p>
      <div>
        <button type="button" class="ghost-button" (click)="cancelDelete()">Cancel</button>
        <button type="button" class="delete-button" (click)="deleteProduct()" [disabled]="isDeleting">
          {{ isDeleting ? 'Deleting...' : 'Delete Product' }}
        </button>
      </div>
    </section>
  </div>
</section>
